import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Component, ReactElement} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {PlainLinkStyledBackButton, StyledSecondaryButton} from '../../../styledComponents/Button'
import {BlockSpacer, currentMedia} from '../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop, DisplayItemInputTitleRequired} from '../../../styledComponents/DisplayItem'
import {ItemHeading, PageTitle, WideWhiteArea} from '../applications/common/resident/NatResidentApplicationsCommon'
import * as React from 'react'
import styled from 'styled-components'
import {Colors, FontSize} from '../../../styledComponents/Constants'
import {CenterRowFlexContainer, ColumnFlexContainer} from '../../../styledComponents/FlexComponents'
import {Messages} from '../../../Messages'
import NatResidentMessageDialogComponent from '../common/resident/NatResidentMessageDialogComponent'
import {MESSAGE_TYPE, TRAINING_MODE_VALID_OR_NOT} from '../../../CodeMaster'
import NatDisasterRepo, {DisasterVoidResponse} from './repository/NatDisasterRepo'
import {DisasterModeInfo} from './repository/NatGetDisasterModeInfo'
import {
    HeadingAreaMessageComponentForResident
} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import {NatGetCurrentDateTime} from '../currentDateTime/repository/NatGetCurrentDateTime'
import moment from 'moment/moment'
import {HKConstants} from '../../../HKConstants'
import {DisasterButton} from './common/NatDisasterCommon'
import {NatDisasterModeSettingLocationState} from './common/NatDisasterModeSettingLocationState'
import PPRadioGroupForResident from '../../../components/shared/forms/resident/PPRadioGroupForResident';
import {ListGridContainer} from '../../../styledComponents/GridComponents';
import {Box} from '@mui/material';

interface Props {
    condominiumCd: string,
    disasterRepo: NatDisasterRepo,
    currentDateTimeRepo: NatCurrentDateTimeRepo,
}

interface State {
    isLoadCompleted: boolean,
    openChangeDialog: boolean,
    cautionChecked: boolean,
    disasterModeInfo: Nullable<DisasterModeInfo>,
    trainingMode: string
}

class NatResidentDisasterModeSettingPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Readonly<Props & ResidentWithErrorHandlingPagePropsSet>) {
        super(props)
        this.state = {
            isLoadCompleted: false,
            openChangeDialog: false,
            cautionChecked: false,
            disasterModeInfo: null,
            trainingMode: ''
        }
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
        this.handleChangeClick = this.handleChangeClick.bind(this)
        this.handleCancelClick = this.handleCancelClick.bind(this)
        this.onChangeCautionChecked = this.onChangeCautionChecked.bind(this)
        this.handleDialogOkClick = this.handleDialogOkClick.bind(this)
        this.handleChangeTrainingMode = this.handleChangeTrainingMode.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {disasterRepo, eventEmitter} = this.props
        await this.props.eventEmitter.showLoading();
        try {
            // 災害時モード設定を取得する(楽観ロック制御のため)
            if (this.props.condominiumCd) {
                const disasterModeSetting = await this.props.asyncCallFunction(
                    disasterRepo.getDisasterMode.bind(disasterRepo, this.props.condominiumCd),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    }
                )
                this.setState({
                    disasterModeInfo: disasterModeSetting
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading();
            this.setState({
                isLoadCompleted: true
            })
        }
    }

    handleBackLinkOnClick(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/disasters/prevention/top`,
            {replace: true}
        )
    }

    handleChangeClick(): void {
        this.setState({openChangeDialog: true})
    }

    handleCancelClick(): void {
        this.setState({openChangeDialog: false})
    }

    onChangeCautionChecked(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({cautionChecked: event.target.checked})
    }

    handleChangeTrainingMode(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            trainingMode: event.target.value
        })
    }

    async handleDialogOkClick(): Promise<void> {
        const {eventEmitter, disasterRepo, condominiumCd} = this.props
        this.setState({openChangeDialog: false})
        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<DisasterVoidResponse> = await this.props.asyncCallFunction(
                this.props.disasterRepo.setDisasterModeResident.bind(
                    disasterRepo, {
                        condominiumCd: condominiumCd,
                        trainingMode: this.state.trainingMode,
                        version: this.state.disasterModeInfo?.version ?? null,
                    }),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            // 現在日時取得および災害時モード終了日の生成
            const currentDateTime: Nullable<NatGetCurrentDateTime> = await this.props.asyncCallFunction(
                this.props.currentDateTimeRepo.getCurrentDateTime.bind(
                    this.props.currentDateTimeRepo),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                })
            let disasterEndDate = '';
            if (currentDateTime && currentDateTime.details !== null) {
                disasterEndDate = moment(currentDateTime.details.currentDateTime).clone()
                    .add(Number(HKConstants.VMN.DIS.SETTING_PERIOD_MONTH), 'month').format('YYYY-MM-DD')
            }

            if (response && response.requestError === null && disasterEndDate) {
                const locationData: NatDisasterModeSettingLocationState = {
                    modeSettingType: 'setting',
                    disasterEndDate: disasterEndDate,
                }
                this.props.navigate(`/resident/condominiums/${condominiumCd}/disasters/mode/complete`,
                    {replace: true, state: locationData}
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderSetDisasterModeDialog(): ReactElement {
        const message: string = Messages.IDISR0001.toString()

        return (
            <NatResidentMessageDialogComponent
                open={this.state.openChangeDialog}
                params={{
                    title: '確認',
                    errorLevel: MESSAGE_TYPE.WARNING,
                    message: message,
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'キャンセル',
                            buttonHandler: this.handleCancelClick,
                        },
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleDialogOkClick,
                        },
                    ],
                }}
            />
        )
    }

    static renderDisasterModeDescription(): ReactElement {
        return (
            <>
                <ItemHeading>災害時モード設定</ItemHeading>
                <DescriptionArea>
                    <DescriptionSubTitle>災害時モードに設定する</DescriptionSubTitle>
                    <BlockSpacer height={'20px'}/>
                    このマンションの素敵ネット利用者と情報共有を行う機能が利用できるようになります。
                    <BlockSpacer height={'20px'}/>
                    【災害時モード設定の目安】
                    <Box pl={'4px'}>
                        <ListGridContainer>
                            <div>・</div>
                            <Box pl={'5px'}>お住まいの自治体から避難指示が発令されるような災害が発生した場合</Box>
                            <div>・</div>
                            <Box pl={'5px'}>災害発生時にマンション内での助け合いが必要な場合</Box>
                            <div>・</div>
                            <Box pl={'5px'}>その他管理組合として必要と判断した場合</Box>
                        </ListGridContainer>
                    </Box>
                </DescriptionArea>
            </>
        )
    }

    renderCaution(): ReactElement {
        return (
            <CautionBlock>
                <CautionArea>
                    <CautionTitle>注意事項</CautionTitle>
                    <BlockSpacer height={'5px'}/>
                    <CautionText>災害時モードに設定すると、このマンションの素敵ネット利用者に通知されます。</CautionText>
                </CautionArea>
                <CautionCheckBoxStyled>
                    <CenterRowFlexContainer>
                        <StyledCheckbox
                            type="checkbox"
                            checked={this.state.cautionChecked}
                            onChange={this.onChangeCautionChecked}
                        />
                        <BlockSpacer width={'5px'}/>
                        注意事項を確認しました
                    </CenterRowFlexContainer>
                </CautionCheckBoxStyled>
            </CautionBlock>
        )
    }

    renderTrainingMode(): ReactElement {
        const {trainingMode} = this.state
        return (
            <>
                <ItemHeading>利用目的</ItemHeading>
                <DescriptionArea>
                    <DisplayItemInputTitleRequired>災害時モードの利用目的を選択</DisplayItemInputTitleRequired>
                    <BlockSpacer height={'10px'}/>
                    <PPRadioGroupForResident
                        data={TRAINING_MODE_VALID_OR_NOT.values()}
                        selectValue={trainingMode}
                        onChange={this.handleChangeTrainingMode}
                        eventKeys={['trainingMode']}
                        eventEmitter={this.props.eventEmitter}
                    />
                </DescriptionArea>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                {this.renderSetDisasterModeDialog()}
                <PlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</PlainLinkStyledBackButton>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <MainTextBlock>
                    <BlockSpacer height={'30px'}/>
                    <PageTitle>
                        災害時モード設定
                    </PageTitle>
                    <BlockSpacer height={'20px'}/>
                    災害時モードの設定または解除を行うことができます。
                    <BlockSpacer height={'20px'}/>
                    <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                </MainTextBlock>
                {NatResidentDisasterModeSettingPage.renderDisasterModeDescription()}
                <BlockSpacer height={'15px'}/>
                {this.renderTrainingMode()}
                {this.renderCaution()}
                <BlockSpacer height={'30px'}/>
                <DisasterButton
                    disabled={!(this.state.cautionChecked && this.state.trainingMode)}
                    onClick={this.handleChangeClick}>
                    災害時モードに設定する
                </DisasterButton>
                <BlockSpacer height={'30px'}/>
                <StyledSecondaryButton onClick={this.handleBackLinkOnClick}>
                    戻る
                </StyledSecondaryButton>
                <BlockSpacer height={'15px'}/>
            </>)
    }
}

export default createResidentWithErrorHandlingPage(NatResidentDisasterModeSettingPage)

const MainTextBlock = styled.div`
    width: 100%;
    ${currentMedia.lessThan('sp')`
    width:100%;
  `}
`
const DescriptionArea = styled(WideWhiteArea)`
    padding: 20px 15px;
`
const DescriptionSubTitle = styled.div`
    font-weight: bold;
`
const CautionBlock = styled(ColumnFlexContainer)`
    padding: 0 15px;
    gap: 20px;
    ${currentMedia.lessThan('sp')`
    padding: 0;
  `}
`
const CautionArea = styled(WideWhiteArea)`
    width: 100%;
    margin-top: 10px;
    padding: 10px;
    ${currentMedia.lessThan('sp')`
    margin-top: 15px;
  `}
`
const CautionTitle = styled.div`
    color: ${Colors.AccentDark} ;
    font-weight: bold;
    font-size: ${FontSize.Middle};
`
const CautionText = styled.div`
    font-size: ${FontSize.Middle};
`
const CautionCheckBoxStyled = styled.label`
    font-size: ${FontSize.Body};
`
const StyledCheckbox = styled.input`
    width: 18px;
    height: 18px;
`